<template>
  <div class="program-countdown-card">
    <div class="countdown-badge">
      CHƯƠNG TRÌNH SẮP DIỄN RA
    </div>

    <h2 class="countdown-title">
      {{ title }}
    </h2>

    <p v-if="formattedStartDate" class="countdown-date">
      Bắt đầu lúc
      <strong>{{ formattedStartDate }}</strong>
    </p>

    <div v-if="invalidDate" class="countdown-message countdown-error">
      Thời gian bắt đầu không hợp lệ.
    </div>

    <div v-else-if="isEnded" class="countdown-message countdown-ended">
      {{ endedText }}
    </div>

    <div
      v-else
      ref="viewport"
      class="countdown-viewport"
    >
      <div
        :id="countdownId"
        ref="flipdown"
        class="flipdown"
      ></div>
    </div>
  </div>
</template>

<script>
/* global FlipDown */
// import FlipDown from "flipdown";
import "flipdown/dist/flipdown.css";

export default {
  name: "FlipCountDown",

  props: {
    /*
     * Các dạng hỗ trợ:
     *
     * "2026-09-01T08:00:00+07:00"
     * "2026-09-01 08:00:00"
     * new Date(...)
     * Unix timestamp giây hoặc millisecond
     */
    startDate: {
      type: [String, Number, Date],
      required: true
    },

    title: {
      type: String,
      default: "Đếm ngược đến thời điểm bắt đầu"
    },

    endedText: {
      type: String,
      default: "Chương trình đã bắt đầu!"
    },

    theme: {
      type: String,
      default: "dark",
      validator(value) {
        return ["dark", "light"].includes(value);
      }
    }
  },

  data() {
    return {
      countdownId:
        "program-flipdown-" +
        Math.random().toString(36).substring(2, 10),

      flipdownInstance: null,
      resizeObserver: null,

      isEnded: false,
      invalidDate: false
    };
  },

  computed: {
    parsedStartDate() {
      return this.parseDate(this.startDate);
    },

    formattedStartDate() {
      const date = this.parsedStartDate;

      if (!date || Number.isNaN(date.getTime())) {
        return "";
      }

      return new Intl.DateTimeFormat("vi-VN", {
        timeZone: "Asia/Ho_Chi_Minh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      }).format(date);
    }
  },

  watch: {
    startDate() {
      this.restartCountdown();
    },

    theme() {
      this.restartCountdown();
    }
  },

  mounted() {
    this.initCountdown();
  },

  beforeDestroy() {
    this.destroyCountdown();
  },

  methods: {
    parseDate(value) {
      if (value instanceof Date) {
        return new Date(value.getTime());
      }

      if (typeof value === "number") {
        /*
         * Nhỏ hơn 1e12 thì coi là Unix timestamp theo giây.
         * Lớn hơn thì coi là millisecond.
         */
        const milliseconds =
          value < 1000000000000
            ? value * 1000
            : value;

        return new Date(milliseconds);
      }

      if (typeof value !== "string" || !value.trim()) {
        return null;
      }

      const input = value.trim();

      /*
       * Nếu backend trả:
       * 2026-09-01 08:00:00
       *
       * Component sẽ mặc định đó là giờ Việt Nam +07:00.
       */
      const vietnamDatePattern =
        /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/;

      if (vietnamDatePattern.test(input)) {
        let normalized = input.replace(" ", "T");

        if (normalized.length === 16) {
          normalized += ":00";
        }

        return new Date(`${normalized}+07:00`);
      }

      /*
       * Các chuỗi đã có timezone:
       * 2026-09-01T08:00:00+07:00
       * 2026-09-01T01:00:00Z
       */
      return new Date(input);
    },

    initCountdown() {
      this.destroyCountdown();

      const targetDate = this.parsedStartDate;

      if (!targetDate || Number.isNaN(targetDate.getTime())) {
        this.invalidDate = true;
        this.isEnded = false;
        return;
      }

      this.invalidDate = false;

      const targetTimestamp = Math.floor(
        targetDate.getTime() / 1000
      );

      const currentTimestamp = Math.floor(
        Date.now() / 1000
      );

      if (targetTimestamp <= currentTimestamp) {
        this.isEnded = true;
        this.$emit("ended");
        return;
      }

      this.isEnded = false;

      this.$nextTick(() => {
        const element = this.$refs.flipdown;

        if (!element) {
          return;
        }

        element.innerHTML = "";

        this.flipdownInstance = new FlipDown(
          targetTimestamp,
          this.countdownId,
          {
            theme: this.theme,
            headings: [
              "Ngày",
              "Giờ",
              "Phút",
              "Giây"
            ]
          }
        );

        this.flipdownInstance
          .start()
          .ifEnded(() => {
            this.isEnded = true;
            this.$emit("ended");
          });

        this.setupResponsive();
      });
    },

    restartCountdown() {
      this.isEnded = false;
      this.invalidDate = false;

      this.$nextTick(() => {
        this.initCountdown();
      });
    },

    destroyCountdown() {
      /*
       * FlipDown không cung cấp hàm destroy riêng,
       * nên dừng interval khi component bị huỷ hoặc khởi tạo lại.
       */
      if (
        this.flipdownInstance &&
        this.flipdownInstance.countdown
      ) {
        clearInterval(
          this.flipdownInstance.countdown
        );
      }

      this.flipdownInstance = null;

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      const element = this.$refs.flipdown;

      if (element) {
        element.innerHTML = "";
        element.style.transform = "";
      }
    },

    setupResponsive() {
      const viewport = this.$refs.viewport;

      if (!viewport) {
        return;
      }

      this.resizeCountdown();

      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.resizeCountdown();
        });

        this.resizeObserver.observe(viewport);
      } else {
        window.addEventListener(
          "resize",
          this.resizeCountdown
        );
      }
    },

    resizeCountdown() {
      const viewport = this.$refs.viewport;
      const flipdown = this.$refs.flipdown;

      if (!viewport || !flipdown) {
        return;
      }

      flipdown.style.transform = "scale(1)";
      viewport.style.height = "auto";

      const countdownWidth = flipdown.offsetWidth;
      const countdownHeight = flipdown.offsetHeight;
      const availableWidth = viewport.clientWidth;

      if (!countdownWidth || !availableWidth) {
        return;
      }

      const scale = Math.min(
        1,
        availableWidth / countdownWidth
      );

      flipdown.style.transform = `scale(${scale})`;
      viewport.style.height =
        `${countdownHeight * scale}px`;
    }
  }
};
</script>

<style>
.program-countdown-card {
  position: relative;
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 30px 24px 26px;
  overflow: hidden;

  background:
    radial-gradient(
      circle at top right,
      rgba(20, 83, 45, 0.09),
      transparent 42%
    ),
    #ffffff;

  border: 1px solid rgba(20, 83, 45, 0.16);
  border-top: 4px solid #14532d;
  border-radius: 20px;

  text-align: center;

  box-shadow:
    0 16px 40px rgba(15, 23, 42, 0.08),
    0 3px 10px rgba(15, 23, 42, 0.04);

  box-sizing: border-box;
}

.countdown-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 12px;
  padding: 6px 12px;

  color: #14532d;
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  border-radius: 999px;

  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.06em;
}

.countdown-title {
  margin: 0;

  color: #14532d;

  font-size: clamp(21px, 3vw, 28px);
  font-weight: 750;
  line-height: 1.3;
}

.countdown-date {
  margin: 8px 0 24px;

  color: #64748b;

  font-size: 15px;
  line-height: 1.5;
}

.countdown-date strong {
  color: #334155;
  font-weight: 650;
}

.countdown-viewport {
  display: flex;
  justify-content: center;
  width: 100%;

  overflow: hidden;
}

.countdown-viewport .flipdown {
  flex: 0 0 auto;
  margin: 0;

  transform-origin: top center;
}

.program-countdown-card
  .flipdown
  .rotor-group-heading::before {
  color: #475569;

  font-size: 12px;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.countdown-message {
  margin-top: 20px;
  padding: 13px 16px;

  border-radius: 12px;

  font-size: 15px;
  font-weight: 700;
}

.countdown-ended {
  color: #166534;
  background: #dcfce7;
  border: 1px solid #86efac;
}

.countdown-error {
  color: #991b1b;
  background: #fee2e2;
  border: 1px solid #fecaca;
}

@media (max-width: 576px) {
  .program-countdown-card {
    padding: 24px 12px 22px;
    border-radius: 16px;
  }

  .countdown-badge {
    margin-bottom: 10px;
    font-size: 10px;
  }

  .countdown-title {
    font-size: 20px;
  }

  .countdown-date {
    margin-bottom: 20px;
    font-size: 13px;
  }
}
</style>